/**
 * ⌘K Results — parallel list `q` (no megendpoint) — plan/35 Wave F + search tokens.
 */
import { getProduct, listProducts } from "@/shared/api/inventory";
import { listPosTickets } from "@/shared/api/pos";
import {
  listPurchaseOrders,
  listToPay,
  listToReceive,
} from "@/shared/api/purchasing";
import { listParties } from "@/shared/api/parties";
import {
  listSalesOrders,
  listToCollect,
  listToDeliver,
} from "@/shared/api/sales";
import { productOnHandTotal } from "@/modules/inventory/product-glance-math";
import { canSeeCompanyNav } from "./nav-math";
import { hasActionPermission } from "./permission-client";
import type { CommandItem } from "./command-math";
import {
  isCompatibleTokenStack,
  objectScopeLockCandidates,
  openListItemForStack,
  openListItemForToken,
  queueScopeLockCandidates,
  type PartySearchToken,
  type QueueSearchToken,
  type SearchToken,
} from "./search-token-math";

const LIMIT = 8;
/** When party-locked, pull a wider page then filter client-side (no partyId on list API yet). */
const SCOPED_LIMIT = 40;

export function recentToCommandItems(
  companySlug: string,
  docs: readonly {
    kind: string;
    id: string;
    label: string;
    path: string;
    hint?: string;
  }[],
): CommandItem[] {
  return docs.map((d) => ({
    id: `recent-${d.kind}-${d.id}`,
    label: d.label,
    hint: d.hint,
    group: "Recent" as const,
    path: d.path.startsWith("/") ? d.path : `/c/${companySlug}${d.path}`,
  }));
}

export async function searchDeskCommandResults(input: {
  companySlug: string;
  held: readonly string[];
  query: string;
  signal?: AbortSignal;
  /** Active search token stack — plan/35 (party + queue|object) */
  tokens?: readonly SearchToken[];
}): Promise<CommandItem[]> {
  const q = input.query.trim();
  const { companySlug, held, signal } = input;
  const tokens = input.tokens ?? [];
  const party = tokens.find((t): t is PartySearchToken => t.type === "party");
  const queue = tokens.find((t): t is QueueSearchToken => t.type === "queue");
  const object = tokens.find((t) => t.type === "object");
  const product = tokens.find((t) => t.type === "product");

  if (party && queue) {
    return searchWithinPartyQueueToken({
      companySlug,
      held,
      query: q,
      signal,
      party,
      queue,
    });
  }

  if (party) {
    return searchWithinPartyToken({
      companySlug,
      held,
      query: q,
      signal,
      token: party,
      /** Offer compatible queue/object locks to stack — plan/35 #5 */
      offerStackLocks: !object && !queue,
    });
  }

  if (product?.type === "product") {
    return searchWithinProductToken({
      companySlug,
      held,
      query: q,
      signal,
      token: product,
    });
  }

  if (object?.type === "object") {
    return searchWithinObjectToken({
      companySlug,
      held,
      query: q,
      signal,
      token: object,
    });
  }

  if (queue) {
    return searchWithinQueueToken({
      companySlug,
      held,
      query: q,
      signal,
      token: queue,
    });
  }

  if (q.length < 1) return [];

  const canSales =
    canSeeCompanyNav(held, "sales") || hasActionPermission(held, "*");
  const canPurchasing =
    canSeeCompanyNav(held, "purchasing") || hasActionPermission(held, "*");
  const canPos = canSeeCompanyNav(held, "pos") || hasActionPermission(held, "*");
  const canProducts =
    canSeeCompanyNav(held, "products") || hasActionPermission(held, "*");

  const objectLocks: CommandItem[] = objectScopeLockCandidates(companySlug, q)
    .filter((c) => {
      if (c.token.object === "salesOrders") return canSales;
      if (c.token.object === "purchaseOrders") return canPurchasing;
      if (c.token.object === "posTickets") return canPos;
      return false;
    })
    .map((c) => ({
      id: c.id,
      label: c.label,
      hint: c.hint,
      group: "Results" as const,
      path: c.path,
      action: { kind: "lockObject" as const, token: c.token },
    }));

  const queueLocks: CommandItem[] = queueScopeLockCandidates(companySlug, q)
    .filter((c) => {
      if (c.token.queue === "toCollect" || c.token.queue === "toDeliver") {
        return canSales;
      }
      if (c.token.queue === "toPay" || c.token.queue === "toReceive") {
        return canPurchasing;
      }
      return false;
    })
    .map((c) => ({
      id: c.id,
      label: c.label,
      hint: c.hint,
      group: "Results" as const,
      path: c.path,
      action: { kind: "lockQueue" as const, token: c.token },
    }));

  const scopeLocks = [...queueLocks, ...objectLocks];

  // Entity/doc search still needs ≥2 chars; scope locks can match "so" / "pay"
  if (q.length < 2) return scopeLocks;

  const tasks: Promise<CommandItem[]>[] = [];

  if (canProducts) {
    tasks.push(
      listProducts(companySlug, { q, limit: LIMIT, signal }).then((page) =>
        (page.products ?? []).map((p) => ({
          id: `hit-prod-${p.id}`,
          label: p.name,
          hint: p.sku ? `Lock · Product · ${p.sku}` : "Lock · Product",
          group: "Results" as const,
          path: `/c/${companySlug}/products/${p.id}`,
          action: {
            kind: "lockProduct" as const,
            token: {
              type: "product" as const,
              id: p.id,
              name: p.name,
              sku: p.sku,
            },
          },
        })),
      ),
    );
  }

  if (canSales) {
    tasks.push(
      listParties(companySlug, {
        role: "customer",
        q,
        limit: LIMIT,
        signal,
      }).then((page) =>
        (page.parties ?? []).map((c) => ({
          id: `hit-cust-${c.id}`,
          label: c.name,
          hint: "Lock · Customer",
          group: "Results" as const,
          path: `/c/${companySlug}/contacts/${c.id}`,
          action: {
            kind: "lockParty" as const,
            token: {
              type: "party" as const,
              role: "customer" as const,
              id: c.id,
              name: c.name,
            },
          },
        })),
      ),
    );
    tasks.push(
      listSalesOrders(companySlug, { q, limit: LIMIT, signal }).then((page) =>
        (page.orders ?? []).map((o) => ({
          id: `hit-so-${o.id}`,
          label: o.number,
          hint: o.customerName,
          group: "Results" as const,
          path: `/c/${companySlug}/sales/orders/${o.id}`,
        })),
      ),
    );
  }
  if (canPurchasing) {
    tasks.push(
      listParties(companySlug, {
        role: "supplier",
        q,
        limit: LIMIT,
        signal,
      }).then((page) =>
        (page.parties ?? []).map((s) => ({
          id: `hit-sup-${s.id}`,
          label: s.name,
          hint: "Lock · Supplier",
          group: "Results" as const,
          path: `/c/${companySlug}/contacts/${s.id}`,
          action: {
            kind: "lockParty" as const,
            token: {
              type: "party" as const,
              role: "supplier" as const,
              id: s.id,
              name: s.name,
            },
          },
        })),
      ),
    );
    tasks.push(
      listPurchaseOrders(companySlug, { q, limit: LIMIT, signal }).then(
        (page) =>
          (page.orders ?? []).map((o) => ({
            id: `hit-po-${o.id}`,
            label: o.number,
            hint: o.supplierName,
            group: "Results" as const,
            path: `/c/${companySlug}/purchasing/orders/${o.id}`,
          })),
      ),
    );
  }
  if (canPos) {
    tasks.push(
      listPosTickets(companySlug, { q, limit: LIMIT, signal }).then((page) =>
        (page.tickets ?? []).map((t) => ({
          id: `hit-pos-${t.id}`,
          label: t.number,
          hint: "POS ticket",
          group: "Results" as const,
          path: `/c/${companySlug}/pos/tickets/${t.id}`,
        })),
      ),
    );
  }

  const settled = await Promise.allSettled(tasks);
  const out: CommandItem[] = [...scopeLocks];
  // Prefer lockable entities first (Photos people / products), then docs — plan/35
  const lockable: CommandItem[] = [];
  const docs: CommandItem[] = [];
  for (const s of settled) {
    if (s.status !== "fulfilled") continue;
    for (const item of s.value) {
      if (
        item.action?.kind === "lockParty" ||
        item.action?.kind === "lockProduct" ||
        item.action?.kind === "lockObject" ||
        item.action?.kind === "lockQueue"
      ) {
        lockable.push(item);
      } else docs.push(item);
    }
  }
  out.push(...lockable, ...docs);
  return out;
}

async function searchWithinPartyToken(input: {
  companySlug: string;
  held: readonly string[];
  query: string;
  signal?: AbortSignal;
  token: PartySearchToken;
  offerStackLocks?: boolean;
}): Promise<CommandItem[]> {
  const { companySlug, held, query, signal, token, offerStackLocks } = input;
  const open = openListItemForToken(companySlug, token);
  const head: CommandItem = {
    id: open.id,
    label: open.label,
    hint: open.hint,
    group: "Results",
    path: open.path,
  };

  const canSales =
    canSeeCompanyNav(held, "sales") || hasActionPermission(held, "*");
  const canPurchasing =
    canSeeCompanyNav(held, "purchasing") || hasActionPermission(held, "*");

  const stackLocks: CommandItem[] = [];
  if (offerStackLocks && query.length >= 1) {
    for (const c of queueScopeLockCandidates(companySlug, query)) {
      if (!isCompatibleTokenStack([token, c.token])) continue;
      if (c.token.queue === "toCollect" || c.token.queue === "toDeliver") {
        if (!canSales) continue;
      } else if (!canPurchasing) continue;
      stackLocks.push({
        id: c.id,
        label: c.label,
        hint: "Lock · Queue",
        group: "Results",
        path: c.path,
        action: { kind: "lockQueue", token: c.token },
      });
    }
    for (const c of objectScopeLockCandidates(companySlug, query)) {
      if (!isCompatibleTokenStack([token, c.token])) continue;
      if (c.token.object === "salesOrders" && !canSales) continue;
      if (c.token.object === "purchaseOrders" && !canPurchasing) continue;
      if (c.token.object === "posTickets") continue;
      stackLocks.push({
        id: c.id,
        label: c.label,
        hint: "Lock · Scope",
        group: "Results",
        path: c.path,
        action: { kind: "lockObject", token: c.token },
      });
    }
  }

  const docs: CommandItem[] = [];
  if (token.role === "customer" && canSales) {
    const page = await listSalesOrders(companySlug, {
      q: query.length >= 1 ? query : undefined,
      limit: SCOPED_LIMIT,
      lifecycle: "open",
      signal,
    });
    for (const o of page.orders ?? []) {
      if (o.customerId !== token.id) continue;
      docs.push({
        id: `hit-so-${o.id}`,
        label: o.number,
        hint: o.customerName,
        group: "Results",
        path: `/c/${companySlug}/sales/orders/${o.id}`,
      });
      if (docs.length >= LIMIT) break;
    }
  } else if (token.role === "supplier" && canPurchasing) {
    const page = await listPurchaseOrders(companySlug, {
      q: query.length >= 1 ? query : undefined,
      limit: SCOPED_LIMIT,
      lifecycle: "open",
      signal,
    });
    for (const o of page.orders ?? []) {
      if (o.supplierId !== token.id) continue;
      docs.push({
        id: `hit-po-${o.id}`,
        label: o.number,
        hint: o.supplierName,
        group: "Results",
        path: `/c/${companySlug}/purchasing/orders/${o.id}`,
      });
      if (docs.length >= LIMIT) break;
    }
  }

  return [head, ...stackLocks, ...docs];
}

async function searchWithinPartyQueueToken(input: {
  companySlug: string;
  held: readonly string[];
  query: string;
  signal?: AbortSignal;
  party: PartySearchToken;
  queue: QueueSearchToken;
}): Promise<CommandItem[]> {
  const { companySlug, held, query, signal, party, queue } = input;
  const open = openListItemForStack(companySlug, [party, queue]);
  const head: CommandItem = {
    id: open.id,
    label: open.label,
    hint: open.hint,
    group: "Results",
    path: open.path,
  };

  const canSales =
    canSeeCompanyNav(held, "sales") || hasActionPermission(held, "*");
  const canPurchasing =
    canSeeCompanyNav(held, "purchasing") || hasActionPermission(held, "*");
  const q = query.length >= 1 ? query : undefined;
  const docs: CommandItem[] = [];

  if (queue.queue === "toCollect" && canSales && party.role === "customer") {
    const page = await listToCollect(companySlug, { q, limit: SCOPED_LIMIT, signal });
    for (const o of page.orders ?? []) {
      if (o.customerId !== party.id) continue;
      docs.push({
        id: `hit-collect-${o.id}`,
        label: o.number,
        hint: o.customerName,
        group: "Results",
        path: `/c/${companySlug}/sales/orders/${o.id}`,
      });
      if (docs.length >= LIMIT) break;
    }
  } else if (queue.queue === "toDeliver" && canSales && party.role === "customer") {
    const page = await listToDeliver(companySlug, { q, limit: SCOPED_LIMIT, signal });
    for (const o of page.orders ?? []) {
      if (o.customerId !== party.id) continue;
      docs.push({
        id: `hit-deliver-${o.id}`,
        label: o.number,
        hint: o.customerName,
        group: "Results",
        path: `/c/${companySlug}/sales/orders/${o.id}`,
      });
      if (docs.length >= LIMIT) break;
    }
  } else if (queue.queue === "toPay" && canPurchasing && party.role === "supplier") {
    const page = await listToPay(companySlug, { q, limit: SCOPED_LIMIT, signal });
    for (const o of page.orders ?? []) {
      if (o.supplierId !== party.id) continue;
      docs.push({
        id: `hit-pay-${o.id}`,
        label: o.number,
        hint: o.supplierName,
        group: "Results",
        path: `/c/${companySlug}/purchasing/orders/${o.id}`,
      });
      if (docs.length >= LIMIT) break;
    }
  } else if (
    queue.queue === "toReceive" &&
    canPurchasing &&
    party.role === "supplier"
  ) {
    const page = await listToReceive(companySlug, {
      q,
      limit: SCOPED_LIMIT,
      signal,
    });
    for (const o of page.orders ?? []) {
      if (o.supplierId !== party.id) continue;
      docs.push({
        id: `hit-receive-${o.id}`,
        label: o.number,
        hint: o.supplierName,
        group: "Results",
        path: `/c/${companySlug}/purchasing/orders/${o.id}`,
      });
      if (docs.length >= LIMIT) break;
    }
  }

  return [head, ...docs];
}

async function searchWithinProductToken(input: {
  companySlug: string;
  held: readonly string[];
  query: string;
  signal?: AbortSignal;
  token: SearchToken & { type: "product" };
}): Promise<CommandItem[]> {
  const { companySlug, held, signal, token } = input;
  const open = openListItemForToken(companySlug, token);
  const head: CommandItem = {
    id: open.id,
    label: open.label,
    hint: open.hint,
    group: "Results",
    path: open.path,
  };

  const canProducts =
    canSeeCompanyNav(held, "products") || hasActionPermission(held, "*");
  if (!canProducts) return [head];

  try {
    const detail = await getProduct(companySlug, token.id);
    if (signal?.aborted) return [head];
    const onHand =
      detail.product.kind === "stocked"
        ? productOnHandTotal(detail.balances)
        : "—";
    const glance: CommandItem = {
      id: `glance-product-${token.id}`,
      label:
        detail.product.kind === "stocked"
          ? `On hand · ${onHand}`
          : "Service · no stock",
      hint: detail.product.sku ?? "Stock glance",
      group: "Results",
      path: open.path,
    };
    return [head, glance];
  } catch {
    return [head];
  }
}

async function searchWithinObjectToken(input: {
  companySlug: string;
  held: readonly string[];
  query: string;
  signal?: AbortSignal;
  token: SearchToken & { type: "object" };
}): Promise<CommandItem[]> {
  const { companySlug, held, query, signal, token } = input;
  const open = openListItemForToken(companySlug, token);
  const head: CommandItem = {
    id: open.id,
    label: open.label,
    hint: open.hint,
    group: "Results",
    path: open.path,
  };

  const canSales =
    canSeeCompanyNav(held, "sales") || hasActionPermission(held, "*");
  const canPurchasing =
    canSeeCompanyNav(held, "purchasing") || hasActionPermission(held, "*");
  const canPos = canSeeCompanyNav(held, "pos") || hasActionPermission(held, "*");

  const docs: CommandItem[] = [];
  if (token.object === "salesOrders" && canSales) {
    const page = await listSalesOrders(companySlug, {
      q: query.length >= 1 ? query : undefined,
      limit: LIMIT,
      lifecycle: query.length >= 1 ? undefined : "open",
      signal,
    });
    for (const o of page.orders ?? []) {
      docs.push({
        id: `hit-so-${o.id}`,
        label: o.number,
        hint: o.customerName,
        group: "Results",
        path: `/c/${companySlug}/sales/orders/${o.id}`,
      });
    }
  } else if (token.object === "purchaseOrders" && canPurchasing) {
    const page = await listPurchaseOrders(companySlug, {
      q: query.length >= 1 ? query : undefined,
      limit: LIMIT,
      lifecycle: query.length >= 1 ? undefined : "open",
      signal,
    });
    for (const o of page.orders ?? []) {
      docs.push({
        id: `hit-po-${o.id}`,
        label: o.number,
        hint: o.supplierName,
        group: "Results",
        path: `/c/${companySlug}/purchasing/orders/${o.id}`,
      });
    }
  } else if (token.object === "posTickets" && canPos) {
    const page = await listPosTickets(companySlug, {
      q: query.length >= 1 ? query : undefined,
      limit: LIMIT,
      signal,
    });
    for (const t of page.tickets ?? []) {
      docs.push({
        id: `hit-pos-${t.id}`,
        label: t.number,
        hint: "POS ticket",
        group: "Results",
        path: `/c/${companySlug}/pos/tickets/${t.id}`,
      });
    }
  }

  return [head, ...docs];
}

async function searchWithinQueueToken(input: {
  companySlug: string;
  held: readonly string[];
  query: string;
  signal?: AbortSignal;
  token: SearchToken & { type: "queue" };
}): Promise<CommandItem[]> {
  const { companySlug, held, query, signal, token } = input;
  const open = openListItemForToken(companySlug, token);
  const head: CommandItem = {
    id: open.id,
    label: open.label,
    hint: open.hint,
    group: "Results",
    path: open.path,
  };

  const canSales =
    canSeeCompanyNav(held, "sales") || hasActionPermission(held, "*");
  const canPurchasing =
    canSeeCompanyNav(held, "purchasing") || hasActionPermission(held, "*");
  const q = query.length >= 1 ? query : undefined;
  const docs: CommandItem[] = [];

  if (token.queue === "toCollect" && canSales) {
    const page = await listToCollect(companySlug, {
      q,
      limit: LIMIT,
      signal,
    });
    for (const o of page.orders ?? []) {
      docs.push({
        id: `hit-collect-${o.id}`,
        label: o.number,
        hint: o.customerName,
        group: "Results",
        path: `/c/${companySlug}/sales/orders/${o.id}`,
      });
    }
  } else if (token.queue === "toDeliver" && canSales) {
    const page = await listToDeliver(companySlug, {
      q,
      limit: LIMIT,
      signal,
    });
    for (const o of page.orders ?? []) {
      docs.push({
        id: `hit-deliver-${o.id}`,
        label: o.number,
        hint: o.customerName,
        group: "Results",
        path: `/c/${companySlug}/sales/orders/${o.id}`,
      });
    }
  } else if (token.queue === "toPay" && canPurchasing) {
    const page = await listToPay(companySlug, { q, limit: LIMIT, signal });
    for (const o of page.orders ?? []) {
      docs.push({
        id: `hit-pay-${o.id}`,
        label: o.number,
        hint: o.supplierName,
        group: "Results",
        path: `/c/${companySlug}/purchasing/orders/${o.id}`,
      });
    }
  } else if (token.queue === "toReceive" && canPurchasing) {
    const page = await listToReceive(companySlug, {
      q,
      limit: LIMIT,
      signal,
    });
    for (const o of page.orders ?? []) {
      docs.push({
        id: `hit-receive-${o.id}`,
        label: o.number,
        hint: o.supplierName,
        group: "Results",
        path: `/c/${companySlug}/purchasing/orders/${o.id}`,
      });
    }
  }

  return [head, ...docs];
}
